import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { SKIP_AUTH_RETRY } from '../auth/auth-context';
import { PlgTokenService } from '../services/plg-token.service';
import { UserSessionService } from '../services/user-session.service';
import { UserSessionStateService } from '../services/user-session-state.service';

/**
 * Recovers from 401s on gated endpoints. Two distinct paths, gated on the
 * BE-supplied `extensions.code` (contract: `app/utils/exception_handlers.py`
 * in dr-plg-service emits `SESSION_EXPIRED` for an elapsed sliding 30-minute
 * TTL and `INVALID_PLG_TOKEN` for an unknown token; bare 401s with no code
 * fall through to the silent-recovery path).
 *
 *  A. `extensions.code === "SESSION_EXPIRED"` — graceful expiry path.
 *     The user is still a valid contact but their server-side wizard state
 *     is gone. Latch `UserSessionStateService.sessionExpired$` so the shell
 *     swaps in `SessionExpiredLandingComponent`; do NOT bootstrap, do NOT
 *     navigate. Wiping local state happens on the landing's "Start over"
 *     CTA — keeping the user in control of when their (now-orphan)
 *     in-memory wizard snapshot is discarded.
 *
 *  B. Any other 401 (no code, `INVALID_PLG_TOKEN`, network-level auth
 *     failure) — silent recovery path. Ask `PlgTokenService` to mint a
 *     fresh session via POST /api/v1/sessions with `Authorization: Bearer …`.
 *     On success the new session is empty server-side: wipe the stale
 *     client-side onboarding progress (`OnboardingCompleted` flag, in-
 *     memory wizard snapshot, `_editSetupMode`) and route the user to
 *     `/onboarding/setup` (the Welcome step, index 0). The original
 *     request is NOT replayed:
 *     replaying against an empty session would return meaningless data,
 *     and the navigation tears down its subscriber anyway. If
 *     `bootstrapSession()` itself fails, `PlgTokenService` has already
 *     cleared local state and raised `signUpRequired$` by the time the
 *     promise rejects.
 *
 * Both paths complete via `EMPTY` so the original caller's `finalize()`
 * blocks tear down loading flags.
 *
 * The bootstrap call itself carries the `SKIP_AUTH_RETRY` HttpContext flag
 * (set by `PlgTokenService.bootstrapSession()`); this interceptor short-
 * circuits on those requests so the recovery doesn't recurse.
 *
 * MUST be registered before ApiInterceptor so any subsequent gated request
 * picks up the freshly minted session id.
 */
@Injectable()
export class SessionRetryInterceptor implements HttpInterceptor {
    // Lazy resolution breaks the DI cycle:
    // HTTP_INTERCEPTORS → PlgTokenService / UserSessionService → HttpClient
    //                  → HTTP_INTERCEPTORS
    private readonly injector = inject(Injector);
    private readonly state = inject(UserSessionStateService);
    private readonly router = inject(Router);

    private get plgToken(): PlgTokenService {
        return this.injector.get(PlgTokenService);
    }
    private get userSession(): UserSessionService {
        return this.injector.get(UserSessionService);
    }

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        // Bootstrap call: skip retry logic entirely. Cleanup on failure is
        // PlgTokenService's responsibility (single source of truth) so the
        // result of POST /sessions just propagates through here.
        if (req.context.get(SKIP_AUTH_RETRY)) {
            return next.handle(req);
        }

        return next.handle(req).pipe(
            catchError((err: unknown) => {
                if (!isUnauthorized(err)) {
                    return throwError(() => err);
                }

                if (isSessionExpired(err)) {
                    // Graceful-expiry path: BE distinguishes "idle TTL elapsed"
                    // from other 401 causes via extensions.code. Show the
                    // landing and wait for the user; don't silently rebuild
                    // state under them — they would otherwise see their
                    // wizard reset to step 0 with no explanation.
                    this.state.notifySessionExpired();
                    return EMPTY;
                }

                return from(this.plgToken.bootstrapSession()).pipe(
                    catchError(() => {
                        // bootstrapSession() already raised signUpRequired$.
                        // Complete silently so the wizard doesn't flash an
                        // error UI behind the landing — and so any
                        // `finalize()` blocks tear down loading flags.
                        return EMPTY;
                    }),
                    switchMap(() => {
                        // Recovery succeeded: server handed us a fresh empty
                        // session. The stale `OnboardingCompleted` flag and
                        // in-memory wizard snapshot would otherwise leave
                        // guards thinking the user is "done" while the
                        // backend has nothing for them — exactly the broken
                        // state this interceptor used to leave behind.
                        this.state.clearOnboardingProgress();
                        this.userSession.clearEditSetupMode();
                        this.router.navigate(['/onboarding/setup']);
                        return EMPTY;
                    }),
                );
            }),
        );
    }
}

function isUnauthorized(err: unknown): boolean {
    return err instanceof HttpErrorResponse && err.status === 401;
}

function isSessionExpired(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) {
        return false;
    }
    const code = (err.error as { extensions?: { code?: unknown } } | null)?.extensions?.code;
    return code === 'SESSION_EXPIRED';
}
